import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageSliceView, ImageSliceViewOpts } from '@play-co/timestep-core/ui';
import { Feature } from 'src/replicant/analytics';
import { Avatar, AvatarOpts } from '../components/shared/Avatar';
import { getTimestampText } from '../visit/visit.util';
import uiConfig from 'src/lib/ui/config';
import type { PlayerProfile } from 'src/replicant/friends/friends.ruleset';
import { withLoading } from 'src/lib/utils';
import { getVisitDetails } from '../visit/visit.sequence';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';

import { AssetGroup } from '@play-co/timestep-core';

namespace style {
  export const popup = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 500,
    darkerBg: false,
  };

  export const message: LangBitmapOpts = {
    size: 30,
    height: 64,
    x: 52,
    y: 89,
    zIndex: 1,
  };
  message.width = popup.width - 2 * message.x!;

  export const underline: ViewOpts = {
    width: message.width,
    y: message.height,
    height: 4,
    backgroundColor: '#FDC381',
  };

  export const cancelButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.cancel,
    labelOffsetY: -1,
    fontSize: 46,
    font: 'Body',
    width: 230,
    height: 100,
    centerOnOrigin: true,
  };
  cancelButton.x = (popup.width - cancelButton.width!) / 2 - 10;
  cancelButton.y = popup.height - 25 - cancelButton.height! / 2;

  export const okButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...cancelButton,
    ...uiConfig.buttons.secondary,
  };
  okButton.x = (popup.width + okButton.width!) / 2 + 10;

  export namespace player {
    export const view: ImageSliceViewOpts = {
      x: 25,
      y: message.y! + message.height! + 25,
      height: 180,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      slices: {
        top: 19,
        left: 19,
        right: 22,
        bot: 28,
      },
    };
    view.width = uiConfig.popups.buruburu.width - 2 * view.x!;

    export const image: AvatarOpts = {
      centerOnOrigin: false,
      width: 128,
      height: 128,
      x: 16,
      y: 25,
    };

    export const name: LangBitmapOpts = {
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
      height: 48,
      x: image.x! + image.width! + 10,
      y: image.y! + 3,
    };
    name.width = view.width - name.x! - 20;

    export const visits: LangBitmapOpts = {
      font: name.font,
      color: name.color,
      x: name.x,
      y: name.y! + name.height!,
      height: 36,
      size: 22,
      align: 'left',
      verticalAlign: 'center',
    };
    visits.width = (view.width - visits.x!) / 2 - 15;

    export const visited: LangBitmapOpts = {
      ...visits,
    };
    visited.y! += visits.height!;

    export const lastLogin: LangBitmapOpts = {
      ...visits,
      align: 'right',
      wordWrap: true,
    };
    const margin = 10;
    lastLogin.width! -= margin;
    lastLogin.x! += visits.width + margin;
    lastLogin.height! *= 2;
  }
}

type CloseHandler = (result: boolean) => void;

export default class PopupConfirmFriendRequest extends PopupBasic {
  private buttonCancel: ButtonScaleViewWithText;
  private buttonOk: ButtonScaleViewWithText;
  private close: CloseHandler;
  private playerPhoto!: Avatar;
  private playerName!: LangBitmapFontTextView;
  private visits!: LangBitmapFontTextView;
  private visited!: LangBitmapFontTextView;
  private lastLogin!: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...style.popup,
      superview: opts.superview,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST._,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts(style.message);

    this.message.addSubview(new View(style.underline));

    this.box.addSubview(this.createPlayerView());

    this.buttonCancel = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.cancelButton,
        localeText: () => i18n('basic.cancel'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.FRIENDS._,
          $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST.CLOSE,
        },
      }),
    );

    this.buttonOk = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.okButton,
        localeText: () => i18n('basic.ok'),
        onClick: async () => {
          sounds.sfx.play('ok');
          return opts.close(true);
        },
        tracking: {
          feature: Feature.FRIENDS._,
          $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST.OK,
        },
      }),
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/shared/frames/scroller_panel_blue.png']);
  }

  public init({ player }: { player: PlayerProfile }) {
    super.init({
      title: i18n('friends.confirmFriendRequest.title'),
      message: i18n('friends.confirmFriendRequest.message'),
    });

    void this.loadPlayerData(player);
  }

  private async loadPlayerData(player: PlayerProfile) {
    const data = await withLoading(() => getVisitDetails(player.id).get(), {
      feature: Feature.FRIENDS._,
      subFeature: Feature.FRIENDS.GET_VISIT_DETAILS,
    });

    this.playerPhoto.updateImage(data?.photo ?? GENERIC_AVATAR);
    this.playerPhoto.setFrame(data?.frame ?? 'default');
    this.playerName.localeText = () => data?.name ?? 'Unknown';
    this.visits.localeText = () =>
      i18n('friendsVisit.confirm.visits', data ?? { visits: 0 });
    this.visited.localeText = () =>
      i18n('friendsVisit.confirm.visited', data ?? { visited: 0 });
    this.lastLogin.localeText = () =>
      `${i18n('friendsVisit.popup.lastLogin')}\n${getTimestampText(
        data?.lastLogin ?? 0,
      )}`;
  }

  private createPlayerView() {
    const view = new ImageSliceView(style.player.view);

    this.playerPhoto = new Avatar({
      ...style.player.image,
      superview: view,
    });

    this.playerName = view.addSubview(
      new LangBitmapFontTextView(style.player.name),
    );

    this.visits = view.addSubview(
      new LangBitmapFontTextView(style.player.visits),
    );

    this.visited = view.addSubview(
      new LangBitmapFontTextView(style.player.visited),
    );

    this.lastLogin = view.addSubview(
      new LangBitmapFontTextView(style.player.lastLogin),
    );

    return view;
  }
}
